import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import { FormLabel, SelectField } from '@silverrailtech/srtlibrary';

import messages from './messages';
import { getRefundDateBoundaries } from './utils';
import OrderDetailsTable from '../OrderDetailsTable/OrderDetailsTable';
import SeasonTable from '../OrderDetailsTable/SeasonTable';
import RefreshIndicator from '../../../components/RefreshIndicator/RefreshIndicator';
import DateTimePicker from '../../../components/DateTimePicker/DateTimePicker';
import { ORIGINS } from '../OrderDetailsTable/constants';
import { MODE_TYPES } from '../AllPartModeSelect/constants';
import localeSettings from '../../../localeSettings';

import inlineStyles from './styles';
import { StyledDatePickerField, StyledDialogContent } from './styled';
import calendarIcon from '../../../images/calendar.svg';

const CalendarIcon = () => (
	<img src={calendarIcon} alt="" width={16} height={16} />
);

// TEMPORARY: unblock deployment by hiding the cancellation/void refund reason dropdowns
// Delete this const and everything it guard below to reinstate.
const TEMP_HIDE_REFUND_REASON_FIELDS = true;

const CancelVoidOrderMode = ({
	isVoid,
	mode,
	formatMessage,
	handleTicketableFareChange,
	data,
	ticketableFaresState,
	handleSelectAll,
	onChangeCancelledDueToStrike,
	handleReleaseSeatChange,
	cancellationReasonTypes,
	cancellationReasonCode,
	isCancellationReasonError,
	handleCancellationReasonChange,
	handleClose,
	handleViewCancelPenalty,
	handleSubmit,
	bookingOrderRevisions,
	isPartialCancel,
	refundReasonTypes = [],
	onRefundReasonChange,
	canSelectCancellationDate = false,
	refundDate = null,
	refundDateError = '',
	seasonStartDate,
	onRefundDateChange,
}) => {
	const processingSubmit = data.get('processingSubmit');
	const refundReason = data.get('refundReason') || '';
	const refundReasonError = data.getIn(['errors', 'refundReason']) || '';
	const orderDetails = data.get('orderDetails');
	const promotionDetails = orderDetails.get('BookingOrderSilverPromos')?.toJS();
	const seasonFareOrderDetails = orderDetails
		.get('seasonFareOrderDetails')
		?.toJS();
	const fareRules = orderDetails.get('BookingLegFareRulesDetails')?.toJS();
	const isSeasonBooking = data.get('isSeasonBooking');
	const isSingleTicketableFares = data.get('isSingleTicketableFares');
	const isReleasable = data.get('isReleasable');
	const enableCancellation =
		ticketableFaresState.some((x) => x.toJS().checked) ||
		isSeasonBooking ||
		!isPartialCancel;
	const isSplitTicketFare = data.get('isSplitTicketFare');
	const expirationDate = !!data.get('expirationDate') && (
		<>
			{' '}
			<FormattedMessage {...messages.lblVoidAllowedUntil} />
			{` ${data.get('expirationDate')}`}
		</>
	);
	const voidCancelClasses = isVoid
		? 'col-4 offset-2 offset-md-4'
		: 'col-4 col-md-4';

	const isUKMarket = data.get('isUKMarket');
	const isVoidDisabled =
		!TEMP_HIDE_REFUND_REASON_FIELDS && isVoid && isUKMarket && !refundReason;
	const hasInvalidRefundDate = canSelectCancellationDate && !!refundDateError;

	const { minRefundDate, maxRefundDate } = useMemo(
		() =>
			getRefundDateBoundaries(
				seasonStartDate,
				formatMessage(localeSettings.dateFormat),
			),
		[seasonStartDate, formatMessage],
	);

	useEffect(() => {
		if (!isPartialCancel) {
			handleSelectAll();
		}
	}, [isPartialCancel]);

	return (
		<>
			<DialogTitle>
				<FormattedMessage
					{...messages[`lbl${isVoid ? 'Void' : 'Cancel'}Title`]}
				/>
				<Typography variant="caption">{expirationDate}</Typography>
			</DialogTitle>
			<StyledDialogContent>
				{!isSeasonBooking ? (
					<OrderDetailsTable
						fareRulesDetails={fareRules}
						isSingleTicketableFares={isSingleTicketableFares}
						orderDetails={orderDetails.toJS()}
						origin={ORIGINS[isVoid ? 'VOID' : 'CANCEL']}
						defaultSelectionState={mode === MODE_TYPES.ALL}
						onTicketableFareChange={handleTicketableFareChange}
						radioTitle={formatMessage(
							messages[isVoid ? 'lblVoidItem' : 'lblCancelItem'],
						)}
						selectedTicketableFareId={data.get('selectedTicketableFareId')}
						ticketableFaresSelectable={ticketableFaresState.toJS()}
						orderPriceKey="ticketableFarePrice"
						handleSelectAll={handleSelectAll}
						promotionDetails={promotionDetails}
						bookingOrderRevisions={bookingOrderRevisions?.toJS() || []}
						isPartialCancel={isPartialCancel}
						isSplitTicketFare={isSplitTicketFare}
					/>
				) : (
					<SeasonTable
						fareRulesDetails={fareRules}
						orderDetails={seasonFareOrderDetails}
						origin="BOOKING"
					/>
				)}

				{canSelectCancellationDate && (
					<StyledDatePickerField
						className={classNames('col-12 col-sm-4', {
							'is-empty': !refundDate,
						})}
						style={inlineStyles.balanceMargin}
					>
						<FormLabel
							id="seasonRefundDate"
							label={formatMessage(messages.lblSeasonRefundDate)}
							isError={!!refundDateError}
						/>
						<DateTimePicker
							id="srtSeasonRefundDate"
							pickerType="Date"
							value={refundDate}
							onChange={onRefundDateChange}
							minDate={minRefundDate || undefined}
							maxDate={maxRefundDate}
							errorText={refundDateError || undefined}
							disabled={processingSubmit}
							openPickerIcon={CalendarIcon}
						/>
					</StyledDatePickerField>
				)}

				{isVoid && isUKMarket && !TEMP_HIDE_REFUND_REASON_FIELDS && (
					<div className="col-12 col-sm-4" style={inlineStyles.balanceMargin}>
						<FormLabel
							id="voidRefundReason"
							label={formatMessage(
								refundReasonError
									? messages.errVoidRefundReasonRequired
									: messages.lblVoidRefundReason,
							)}
							isError={!!refundReasonError}
						/>
						<SelectField
							name="srtVoidRefundReason"
							id="srtVoidRefundReason"
							value={refundReason}
							onChange={onRefundReasonChange}
							options={refundReasonTypes.map((reason) => ({
								name: reason.name,
								value: reason.code,
							}))}
							displayEmpty
							placeholder={formatMessage(
								messages.lblVoidRefundReasonPlaceholder,
							)}
							error={!!refundReasonError}
						/>
					</div>
				)}

				{!isVoid && data.get('orderCanBeCancelDueToStrike') && (
					<div className="col-12" style={inlineStyles.balanceMargin}>
						<div className="col-12" style={inlineStyles.box}>
							<FormattedMessage {...messages.lblNotes} />
						</div>
						<div className="col-12" style={inlineStyles.subHeader}>
							<Switch
								id="cancelledDueToStrike"
								onChange={onChangeCancelledDueToStrike}
							/>
							<FormattedMessage {...messages.lblCancelDueToStrike} />
						</div>
						<div className="col-12" style={inlineStyles.subHeader}>
							{isReleasable === true ? (
								<>
									<Switch
										id="releaseSeatChange"
										onChange={handleReleaseSeatChange}
									/>
									<FormattedMessage {...messages.lblReleaseInventoryOnly} />
								</>
							) : (
								<FormattedMessage {...messages.lblCannotRelease} />
							)}
						</div>
					</div>
				)}
				{!isVoid && isUKMarket && !TEMP_HIDE_REFUND_REASON_FIELDS && (
					<div className="col-12 col-sm-4" style={inlineStyles.balanceMargin}>
						<FormLabel
							id="cancelReason"
							label={formatMessage(
								isCancellationReasonError
									? messages.errCancellationRefundReasonRequired
									: messages.lblCancellationRefundReason,
							)}
							isError={isCancellationReasonError}
						/>
						<SelectField
							name="srtCancellationReasonCode"
							id="srtCancellationReasonCode"
							value={cancellationReasonCode}
							onChange={handleCancellationReasonChange}
							options={cancellationReasonTypes.map((reason) => ({
								name: reason.name,
								value: reason.code,
							}))}
							displayEmpty
							placeholder={formatMessage(
								messages.lblCancellationRefundReasonPlaceholder,
							)}
							error={isCancellationReasonError}
						/>
					</div>
				)}
			</StyledDialogContent>
			<DialogActions>
				<div className="row" style={inlineStyles.dialogActionsRoot}>
					<div className={voidCancelClasses}>
						<Button
							variant="contained"
							id={`srt${isVoid ? 'Void' : 'Cancel'}OrderClose`}
							onClick={handleClose}
							fullWidth
							disabled={processingSubmit}
						>
							<FormattedMessage {...messages.btnClose} />
						</Button>
					</div>
					{!isVoid && (
						<div className="col-4 col-md-4">
							<Button
								variant="contained"
								id="ViewCancellationPenalty"
								onClick={handleViewCancelPenalty}
								fullWidth
								color="secondary"
								disabled={
									!enableCancellation ||
									processingSubmit ||
									hasInvalidRefundDate
								}
							>
								<FormattedMessage {...messages.btnViewCancelPenalty} />
							</Button>
						</div>
					)}
					<div className="col-4 col-md-4">
						{data.get('processingSubmit') ? (
							<RefreshIndicator size={36} left={40} top={0} status="loading" />
						) : (
							<Button
								variant="contained"
								id={`srtRequest${isVoid ? 'Void' : 'Cancellation'}`}
								onClick={handleSubmit}
								fullWidth
								color="primary"
								disabled={
									!enableCancellation || isVoidDisabled || hasInvalidRefundDate
								}
							>
								<FormattedMessage
									{...messages[`btn${isVoid ? 'Void' : 'Cancel'}Submit`]}
								/>
							</Button>
						)}
					</div>
				</div>
			</DialogActions>
		</>
	);
};

CancelVoidOrderMode.propTypes = {
	isVoid: PropTypes.bool.isRequired,
	mode: PropTypes.string.isRequired,
	formatMessage: PropTypes.func.isRequired,
	handleTicketableFareChange: PropTypes.func.isRequired,
	data: PropTypes.object.isRequired,
	ticketableFaresState: PropTypes.object.isRequired,
	handleSelectAll: PropTypes.func.isRequired,
	onChangeCancelledDueToStrike: PropTypes.func.isRequired,
	handleReleaseSeatChange: PropTypes.func.isRequired,
	cancellationReasonTypes: PropTypes.array.isRequired,
	cancellationReasonCode: PropTypes.string.isRequired,
	isCancellationReasonError: PropTypes.bool.isRequired,
	handleCancellationReasonChange: PropTypes.func.isRequired,
	handleClose: PropTypes.func.isRequired,
	handleViewCancelPenalty: PropTypes.func.isRequired,
	handleSubmit: PropTypes.func.isRequired,
	bookingOrderRevisions: PropTypes.object,
	isPartialCancel: PropTypes.bool,
	refundReasonTypes: PropTypes.arrayOf(
		PropTypes.shape({
			code: PropTypes.string,
			name: PropTypes.string,
		}),
	),
	onRefundReasonChange: PropTypes.func,
	canSelectCancellationDate: PropTypes.bool,
	refundDate: PropTypes.oneOfType([
		PropTypes.instanceOf(Date),
		PropTypes.number,
		PropTypes.string,
	]),
	refundDateError: PropTypes.string,
	seasonStartDate: PropTypes.string,
	onRefundDateChange: PropTypes.func,
};

export default CancelVoidOrderMode;
